import * as React from 'react';

import { numberToPrettyString } from '@cian/utils/lib/shared/number_helpers';
import { mergeStyles } from '@cian/utils/lib/shared/style';

import { IOfferSchema } from '@cian/api-models/offer-card/v1/get-offer-data';
import { Rating } from '../../common/components/rating';
import { getOfferType, isCommercial } from '../../utils/offer_type';
import { getHouseMaterialType } from './helpers';

import * as styles from './index.css';

export type THouseMaterial =
  | 'block'
  | 'boards'
  | 'brick'
  | 'custom'
  | 'monolith'
  | 'monolithBrick'
  | 'none'
  | 'old'
  | 'panel'
  | 'stalin'
  | 'wood';

export interface IRatingHouse {
  flatQuality?: number;
  houseQuality?: number;
}

export interface IRatingLocation {
  ecologyIndex?: number;
  infrastructureIndex?: number;
  safetyIndex?: number;
  surroundBad?: number;
  surroundPlus?: number;
  transportIndex?: number;
}

export interface IBtiHouseData {
  entrances?: number;
  flatCount?: number;
  floorMax?: number;
  houseMaterialType?: THouseMaterial;
  isEmergency?: boolean;
  nalog?: number;
  priceCadastr?: number;
  priceCian?: number;
  priceSqm?: number;
  priceSqmDiff?: number;
  priceSqmMaxCadastr?: number;
  ratingHouse?: IRatingHouse;
  ratingLocation?: IRatingLocation;
  roomRentPrice?: number;
  roomRentPriceDiff?: number;
  urlCalculatorPriceCian?: string;
  yearRelease?: number;
}

type THouseData = IBtiHouseData | null;

export interface IBtiCommercialHouseDataProps {
  houseData: THouseData;
  jkName: string;
  roomCount: number | null;
  offer: IOfferSchema;
}

export class BtiCommercialHouseData extends React.Component<IBtiCommercialHouseDataProps, {}> {
  public render() {
    /**
     * Иногда бывает что внутри houseData приходит только 1 поле priceCian,
     * которое используется в другом компоненте, но все остальные поля приходят как null.
     */
    if (checkBtiHouseDataHidden(this.props.houseData)) {
      return null;
    }

    const {
      entrances,
      flatCount,
      floorMax,
      houseMaterialType,
      isEmergency,
      priceSqm,
      priceSqmDiff,
      ratingHouse,
      roomRentPrice,
      roomRentPriceDiff,
      yearRelease,
    } = this.props.houseData as IBtiHouseData;

    const offerType = getOfferType(this.props.offer);
    const isCommercialCard = isCommercial(offerType);

    let label = '';

    /**
     * Для коммерческой карточки рядом с "Динамика цены за кв.м. за год" выводим пометку - жилая
     */
    if (isCommercialCard) {
      label = 'жилая';
    }

    return (
      <div className={styles['container']}>
        <div className={styles['title']}>{this.props.jkName ? `ЖК \u00ab${this.props.jkName}\u00bb` : 'О доме'}</div>
        <div className={styles['content']}>
          <div className={styles['column']}>
            <div className={styles['item']}>
              <div className={styles['name']}>Год постройки</div>
              <div className={styles['value']}>{!!yearRelease ? yearRelease : '-'}</div>
            </div>
            <div className={styles['item']}>
              <div className={styles['name']}>Материалы стен</div>
              <div className={styles['value']}>
                {!!houseMaterialType && houseMaterialType !== 'none' && houseMaterialType !== 'custom'
                  ? getHouseMaterialType(houseMaterialType)
                  : '-'}
              </div>
            </div>
            <div className={styles['item']}>
              <div className={styles['name']}>Этажность</div>
              <div className={styles['value']}>{!!floorMax ? floorMax : '-'}</div>
            </div>
            <div className={styles['item']}>
              <div className={styles['name']}>Подъездов</div>
              <div className={styles['value']}>{!!entrances ? entrances : '-'}</div>
            </div>
            <div className={styles['item']}>
              <div className={styles['name']}>Квартир</div>
              <div className={styles['value']}>{!!flatCount ? flatCount : '-'}</div>
            </div>
            <div className={styles['item']}>
              <div className={styles['name']}>
                Средняя цена за м<sup>2</sup>
              </div>
              <div className={styles['value']}>{!!priceSqm ? `${numberToPrettyString(priceSqm)} \u20BD` : '-'}</div>
            </div>
          </div>
          <div className={styles['column']}>
            <div className={styles['item']}>
              <div className={styles['name']}>
                Динамика цены за м<sup>2</sup> за год {label}
              </div>
              <div
                {...mergeStyles(
                  styles['value'],
                  !!priceSqmDiff ? (priceSqmDiff > 0 ? styles['positive'] : styles['negative']) : '',
                )}
              >
                {!!priceSqmDiff ? `${priceSqmDiff > 0 ? '+' : ''} ${toFixedPrettyDecimal(priceSqmDiff)} %` : '-'}
              </div>
            </div>
            {!!this.props.roomCount && (
              <div className={styles['item']}>
                <div className={styles['name']}>
                  Ставка аренды {this.props.roomCount > 4 ? 'многокомн.\u00a0кв.' : `${this.props.roomCount}-комн.кв.`}
                </div>
                <div className={styles['value']}>
                  {!!roomRentPrice ? `${numberToPrettyString(roomRentPrice)} \u20BD/мес` : '-'}
                </div>
              </div>
            )}
            <div className={styles['item']}>
              <div className={styles['name']}>Динамика ставки за год {label}</div>
              <div
                {...mergeStyles(
                  styles['value'],
                  !!roomRentPriceDiff ? (roomRentPriceDiff > 0 ? styles['positive'] : styles['negative']) : '',
                )}
              >
                {!!roomRentPriceDiff
                  ? `${roomRentPriceDiff > 0 ? '+' : ''} ${toFixedPrettyDecimal(roomRentPriceDiff)} %`
                  : '-'}
              </div>
            </div>
            <div className={styles['item']}>
              <div className={styles['name']}>Аварийный</div>
              <div className={styles['value']}>
                {// tslint:disable-next-line:strict-type-predicates
                isEmergency !== undefined && isEmergency !== null ? (isEmergency ? 'Да' : 'Нет') : '-'}
              </div>
            </div>
            <div className={styles['item']}>
              <div className={styles['name']}>Конструктив и состояние</div>
              <div {...mergeStyles(styles['value'], styles['rating'])}>
                {!!ratingHouse && ratingHouse.houseQuality ? <Rating rating={ratingHouse.houseQuality} /> : '-'}
              </div>
            </div>
            <div className={styles['item']}>
              <div className={styles['name']}>Квартиры и планировки</div>
              <div {...mergeStyles(styles['value'], styles['rating'])}>
                {!!ratingHouse && ratingHouse.flatQuality ? <Rating rating={ratingHouse.flatQuality} /> : '-'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function toFixedPrettyDecimal(decimal: number) {
  const fixedDecimal = Number(decimal.toFixed(2));

  return numberToPrettyString(fixedDecimal);
}

export function checkBtiHouseDataHidden(houseData: THouseData): boolean {
  return (
    !houseData ||
    (houseData.houseMaterialType === 'none' &&
      !houseData.entrances &&
      !houseData.flatCount &&
      !houseData.floorMax &&
      !houseData.isEmergency &&
      !houseData.priceSqm &&
      !houseData.priceSqmDiff &&
      !houseData.ratingHouse &&
      !houseData.roomRentPrice &&
      !houseData.roomRentPriceDiff &&
      !houseData.yearRelease)
  );
}
